import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  Divider,
  Box,
  RadioGroup,
  Radio,
  Avatar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

// Styled components for better layout
const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export interface KioskReceiptDisplaySettings {
  companyName: string;
  companyAddress: string;
  taxNumber: string;
  companyPhone: string; // ✅ YENİ: Telefon numarası
  companyWebsite: string; // ✅ YENİ: Web sitesi
  mersisNumber: string; // ✅ YENİ: Mersis numarası
  showReceiptHeader: boolean;
  showVat: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumberResetValue: number;
  resetAtEndOfDay: boolean;
  printCopyCount: number;
  productGrouping: string;
  standardFooterNote: string;
  thankYouMessage: string;
  receiptWidth: number;
  characterLength: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  headerType: 'text' | 'logo'; // Başlık tipi: metin mi logo mu
  logoUrl: string; // Logo URL'si (Firebase Storage'dan)
}

interface KioskReceiptSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (settings: KioskReceiptDisplaySettings) => void;
  initialSettings: KioskReceiptDisplaySettings;
  onImageUpload?: (file: File) => Promise<string>; // Logo yükleme fonksiyonu
}

const KioskReceiptSettingsModal: React.FC<KioskReceiptSettingsModalProps> = ({
  open,
  onClose,
  onSave,
  initialSettings,
  onImageUpload,
}) => {
  const { t } = useTranslation('salesSystem');
  const [formValues, setFormValues] = React.useState<KioskReceiptDisplaySettings>(initialSettings);
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setFormValues({
        ...initialSettings,
        showCashierName: typeof initialSettings.showCashierName === 'boolean' ? initialSettings.showCashierName : false,
        showReceiptNumber: typeof initialSettings.showReceiptNumber === 'boolean' ? initialSettings.showReceiptNumber : false,
        headerType: initialSettings.headerType || 'text',
        logoUrl: initialSettings.logoUrl || '',
      });
      setErrorMessage(null); // Hata mesajını temizle
    }
  }, [open, initialSettings]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;
    if (type === 'checkbox') {
      const { checked } = event.target as HTMLInputElement;
      setFormValues(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormValues(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } 
    else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleNumberChange = (name: keyof KioskReceiptDisplaySettings, value: string | number) => {
    setFormValues(prev => ({ ...prev, [name]: typeof value === 'string' ? parseFloat(value) || 0 : value }));
  };

  // Logo yükleme fonksiyonu
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onImageUpload) {
      console.log('⚠️ [KIOSK RECEIPT] Logo yükleme iptal:', { 
        file: !!file, 
        onImageUpload: !!onImageUpload 
      });
      return;
    }

    console.log('📤 [KIOSK RECEIPT] Logo yükleme başlatıldı:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    // Dosya boyutu kontrolü (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage(t('kioskReceiptModal.errors.logoTooLarge'));
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      setErrorMessage(t('kioskReceiptModal.errors.onlyImageAllowed'));
      return;
    }

    try {
      setLogoUploading(true);
      setErrorMessage(null);
      
      console.log('🔄 [KIOSK RECEIPT] onImageUpload fonksiyonu çağrılıyor...');
      const logoUrl = await onImageUpload(file);
      console.log('✅ [KIOSK RECEIPT] Logo URL alındı:', logoUrl);
      
      setFormValues(prev => ({ ...prev, logoUrl }));
      
      console.log('✅ [KIOSK RECEIPT] Logo başarıyla yüklendi:', logoUrl);
    } catch (error: any) {
      console.error('❌ [KIOSK RECEIPT] Logo yükleme hatası:', error);
      setErrorMessage(t('kioskReceiptModal.errors.logoUploadFailed', { message: error.message }));
    } finally {
      setLogoUploading(false);
    }
  };

  // Logo silme fonksiyonu
  const handleLogoDelete = () => {
    setFormValues(prev => ({ ...prev, logoUrl: '' }));
  };

  // Başlık tipi değişikliği
  const handleHeaderTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newHeaderType = event.target.value as 'text' | 'logo';
    setFormValues(prev => ({ ...prev, headerType: newHeaderType }));
    
    // Eğer logo seçiliyorsa showReceiptHeader'ı true yap
    if (newHeaderType === 'logo') {
      setFormValues(prev => ({ ...prev, showReceiptHeader: true }));
    }
  };

  const handleSave = () => {
    // Basic validation - Sadece text başlık tipinde şirket adı zorunlu
    if (formValues.headerType === 'text' && !formValues.companyName) {
      console.error("❌ [KIOSK RECEIPT] Metin başlık tipinde şirket adı zorunludur");
      setErrorMessage(t('kioskReceiptModal.errors.companyNameRequiredForTextHeader'));
      return;
    }
    
    // Logo başlık tipinde logo kontrolü
    if (formValues.headerType === 'logo' && !formValues.logoUrl) {
      console.error("❌ [KIOSK RECEIPT] Logo başlık tipinde logo yüklenmesi zorunludur");
      setErrorMessage(t('kioskReceiptModal.errors.logoRequiredForLogoHeader'));
      return;
    }
    
    console.log('💾 [KIOSK RECEIPT] Adisyon ayarları kaydediliyor:', formValues);
    setErrorMessage(null); // Hata mesajını temizle
    onSave(formValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {t('kioskReceiptModal.title')}
        <GuideButton guideKey="sales.kioskReceiptSettings" size="small" />
      </DialogTitle>
      <DialogContent dividers>
        {/* Genel Hata Mesajı */}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}
        
        <StyledForm>
          <Typography variant="h6" gutterBottom>{t('kioskReceiptModal.sections.receiptHeaderSettings')}</Typography>
          
          {/* Başlık Tipi Seçimi */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{t('kioskReceiptModal.fields.selectHeaderType')}</Typography>
            <RadioGroup
              row
              value={formValues.headerType}
              onChange={handleHeaderTypeChange}
            >
              <FormControlLabel 
                value="text" 
                control={<Radio />} 
                label={t('kioskReceiptModal.fields.textHeader')} 
              />
              <FormControlLabel 
                value="logo" 
                control={<Radio />} 
                label={t('kioskReceiptModal.fields.logoHeader')} 
              />
            </RadioGroup>
          </Box>

          {/* Metin Başlık Bölümü */}
          {formValues.headerType === 'text' && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('kioskReceiptModal.sections.companyInfo')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="companyName"
                    label={t('kioskReceiptModal.fields.companyName')}
                    value={formValues.companyName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="taxNumber"
                    label={t('kioskReceiptModal.fields.taxNumber')}
                    value={formValues.taxNumber}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="mersisNumber"
                    label={t('kioskReceiptModal.fields.mersisNumber')}
                    value={formValues.mersisNumber}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="companyPhone"
                    label={t('kioskReceiptModal.fields.companyPhone')}
                    value={formValues.companyPhone}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={t('kioskReceiptModal.fields.phonePlaceholder')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="companyWebsite"
                    label={t('kioskReceiptModal.fields.companyWebsite')}
                    value={formValues.companyWebsite}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={t('kioskReceiptModal.fields.websitePlaceholder')}
                  />
                </Grid>
              </Grid>
              <TextField
                name="companyAddress"
                label={t('kioskReceiptModal.fields.companyAddress')}
                value={formValues.companyAddress}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {/* Logo Başlık Bölümü */}
          {formValues.headerType === 'logo' && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('kioskReceiptModal.sections.logoUpload')}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {formValues.logoUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={formValues.logoUrl}
                      alt={t('kioskReceiptModal.fields.logoAlt')}
                      sx={{ width: 80, height: 80 }}
                      variant="rounded"
                    />
                    <Box>
                      <Typography variant="body2" color="success.main" gutterBottom>
                        {t('kioskReceiptModal.messages.logoUploaded')}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleLogoDelete}
                      >
                        {t('kioskReceiptModal.actions.deleteLogo')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="kiosk-logo-upload-button"
                      type="file"
                      onChange={handleLogoUpload}
                      disabled={logoUploading || !onImageUpload}
                    />
                    <label htmlFor="kiosk-logo-upload-button">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={logoUploading || !onImageUpload}
                      >
                        {logoUploading ? t('kioskReceiptModal.actions.uploading') : t('kioskReceiptModal.actions.uploadLogo')}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      {t('kioskReceiptModal.fields.logoHelp')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                name="showReceiptHeader"
                checked={formValues.showReceiptHeader}
                onChange={handleChange}
                disabled={formValues.headerType === 'logo'}
              />
            }
            label={t('kioskReceiptModal.fields.showReceiptHeader')}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('kioskReceiptModal.sections.printSettings')}</Typography>
          <FormControlLabel
            control={<Checkbox name="showVat" checked={formValues.showVat} onChange={handleChange} />}
            label={t('kioskReceiptModal.fields.showVat')}
          />
          <TextField
            name="printCopyCount"
            label={t('kioskReceiptModal.fields.printCopyCount')}
            type="number"
            value={formValues.printCopyCount}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value) && value >= 1 && value <= 9) {
                handleNumberChange('printCopyCount', value);
              }
            }}
            fullWidth
            variant="outlined"
            size="small"
            helperText={t('kioskReceiptModal.fields.printCopyCountHelp')}
            InputProps={{ 
              inputProps: { min: 1, max: 9 }
            }}
            sx={{ mt: 2 }}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('kioskReceiptModal.sections.footerSettings')}</Typography>
          <TextField
            name="standardFooterNote"
            label={t('kioskReceiptModal.fields.standardFooterNote')}
            value={formValues.standardFooterNote}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            size="small"
          />
          <TextField
            name="thankYouMessage"
            label={t('kioskReceiptModal.fields.thankYouMessage')}
            value={formValues.thankYouMessage}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            size="small"
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('kioskReceiptModal.sections.receiptFormat')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="receiptWidth"
                label={t('kioskReceiptModal.fields.receiptWidth')}
                type="number"
                value={formValues.receiptWidth}
                onChange={(e) => handleNumberChange('receiptWidth', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="characterLength"
                label={t('kioskReceiptModal.fields.characterLength')}
                type="number"
                value={formValues.characterLength}
                onChange={(e) => handleNumberChange('characterLength', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
          
          <Typography variant="subtitle1" gutterBottom sx={{mt: 2}}>{t('kioskReceiptModal.sections.pageMargins')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginTop"
                label={t('kioskReceiptModal.fields.marginTop')}
                type="number"
                value={formValues.marginTop}
                onChange={(e) => handleNumberChange('marginTop', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginBottom"
                label={t('kioskReceiptModal.fields.marginBottom')}
                type="number"
                value={formValues.marginBottom}
                onChange={(e) => handleNumberChange('marginBottom', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginLeft"
                label={t('kioskReceiptModal.fields.marginLeft')}
                type="number"
                value={formValues.marginLeft}
                onChange={(e) => handleNumberChange('marginLeft', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginRight"
                label={t('kioskReceiptModal.fields.marginRight')}
                type="number"
                value={formValues.marginRight}
                onChange={(e) => handleNumberChange('marginRight', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </StyledForm>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('kioskReceiptModal.actions.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">{t('kioskReceiptModal.actions.save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default KioskReceiptSettingsModal;

